/**
 * ****************************************************************************
 *
 * INVICARA INC CONFIDENTIAL __________________
 *
 * Copyright (C) [2012] - [2019] INVICARA INC, INVICARA Pte Ltd, INVICARA INDIA
 * PVT LTD All Rights Reserved.
 *
 * NOTICE: All information contained herein is, and remains the property of
 * Invicara Inc and its suppliers, if any. The intellectual and technical
 * concepts contained herein are proprietary to Invicara Inc and its suppliers
 * and may be covered by U.S. and Foreign Patents, patents in process, and are
 * protected by trade secret or copyright law. Dissemination of this information
 * or reproduction of this material is strictly forbidden unless prior written
 * permission is obtained from Invicara Inc.
 */

import React, { useState, useEffect, useCallback, useMemo } from "react";
import _ from 'lodash'
import clsx from 'clsx'
import Select from "react-select"

import { IafUserGroup, IafDataSource, IafPassSvc } from '@dtplatform/platform-api'
import { ScriptHelper } from "@invicara/ipa-core/esm_modules/IpaUtils"
import { useAuth } from "../../contexts/AuthContext";

import { copyToClipboard } from "../utils/dev-mgmt-utils";

import './DevUserGroupView.css'

// Select styles for dark theme
const selectStyles = {
  control: (base, state) => ({
    ...base,
    backgroundColor: 'rgba(255, 255, 255, 0.05)',
    borderColor: state.isFocused ? '#10b981' : 'rgba(255, 255, 255, 0.1)',
    boxShadow: state.isFocused ? '0 0 0 3px rgba(16, 185, 129, 0.15)' : 'none',
    '&:hover': { borderColor: 'rgba(255, 255, 255, 0.2)' },
    minHeight: '42px'
  }),
  menu: (base) => ({
    ...base,
    backgroundColor: '#1a1a2e',
    border: '1px solid rgba(255, 255, 255, 0.1)',
    zIndex: 100
  }),
  option: (base, state) => ({
    ...base,
    backgroundColor: state.isSelected ? '#10b981' : state.isFocused ? 'rgba(16, 185, 129, 0.2)' : 'transparent',
    color: 'white',
    cursor: 'pointer'
  }),
  multiValue: (base) => ({
    ...base,
    backgroundColor: 'rgba(16, 185, 129, 0.2)',
    borderRadius: '4px'
  }),
  multiValueLabel: (base) => ({
    ...base,
    color: '#10b981'
  }),
  multiValueRemove: (base) => ({
    ...base,
    color: '#10b981',
    '&:hover': { backgroundColor: 'rgba(16, 185, 129, 0.3)', color: 'white' }
  }),
  placeholder: (base) => ({ ...base, color: 'rgba(255, 255, 255, 0.4)' }),
  singleValue: (base) => ({ ...base, color: 'white' }),
  input: (base) => ({ ...base, color: 'white' })
};

const DevUserGroupView = ({ handler, onLoadComplete, selectedItems, user }) => {
  // Get auth context for API calls
  const { createCtx } = useAuth();
  
  // State
  const [userGroups, setUserGroups] = useState([]);
  const [allUserGroupNamesUpper, setAllUserGroupNamesUpper] = useState([]);
  const [selectedUserGroup, setSelectedUserGroup] = useState(null);
  const [loadingGroups, setLoadingGroups] = useState(true);
  const [loadingUsers, setLoadingUsers] = useState(false);

  // Users state
  const [usersInSelectedGroup, setUsersInSelectedGroup] = useState([]);
  const [filteredUsers, setFilteredUsers] = useState([]);
  const [totalUsers, setTotalUsers] = useState(0);
  const [filterBy, setFilterBy] = useState('');

  // Invites state
  const [invitesInSelectedGroup, setInvitesInSelectedGroup] = useState([]);
  const [expiredInvites, setExpiredInvites] = useState([]);
  const [loadingInvites, setLoadingInvites] = useState(false);

  // Edit state
  const [editingName, setEditingName] = useState(false);
  const [editedName, setEditedName] = useState('');
  const [nameError, setNameError] = useState(null);
  const [savingName, setSavingName] = useState(false);

  // Academy/Thinkific state
  const [fetchingMembership, setFetchingMembership] = useState(false);
  const [freeBundleEnrollees, setFreeBundleEnrollees] = useState([]);
  const [paidBundleEnrollees, setPaidBundleEnrollees] = useState([]);

  // Add user form state
  const [showAddForm, setShowAddForm] = useState(false);
  const [addFormFirstname, setAddFormFirstname] = useState('');
  const [addFormLastname, setAddFormLastname] = useState('');
  const [addFormEmail, setAddFormEmail] = useState('');
  const [addFormEmails, setAddFormEmails] = useState([]);
  const [addFormGroups, setAddFormGroups] = useState([]);
  const [addingUsers, setAddingUsers] = useState(false);
  const [addFormError, setAddFormError] = useState(null);
  const [addFormSuccess, setAddFormSuccess] = useState(null);

  // Delete confirmation state
  const [deletingUser, setDeletingUser] = useState(null);

  // Config shortcuts
  const config = handler?.config || {};
  const scripts = config.scripts || {};

  // Load user groups on mount
  useEffect(() => {
    loadUserGroups();
    onLoadComplete?.();
  }, []);

  // Load users when group changes
  useEffect(() => {
    if (selectedUserGroup) {
      loadUsersForGroup();
    }
  }, [selectedUserGroup]);

  // Filter users when filter changes
  useEffect(() => {
    applyFilter();
  }, [filterBy, usersInSelectedGroup, freeBundleEnrollees, paidBundleEnrollees]);

  const loadUserGroups = async () => {
    setLoadingGroups(true);
    try {
      let groups = await ScriptHelper.executeScript(scripts.fetchGroups);
      groups = groups || [];
      const namesUpper = groups.map(g => g._name.toUpperCase());
      setAllUserGroupNamesUpper(namesUpper);
      setUserGroups(groups);
      if (groups.length > 0) {
        setSelectedUserGroup(groups[0]);
      }
      // Load academy memberships if internal groups
      if (config.useInternalGroups) {
        loadAcademyMemberships();
      }
    } catch (err) {
      console.error('Error loading user groups:', err);
    } finally {
      setLoadingGroups(false);
    }
  };

  const loadAcademyMemberships = async () => {
    try {
      setFetchingMembership(true);
      const orchs = await IafDataSource.getOrchestrators();
      const bundleOrch = orchs._list?.find(o => o._userType === "think_read_bundle");
      
      if (!bundleOrch) {
        setFetchingMembership(false);
        return;
      }

      const bundleTypes = ['free', 'paid'];
      
      for (const bundleType of bundleTypes) {
        let page = 1;
        let totalPages = 1;
        const allEnrollees = [];

        while (page <= totalPages) {
          const orchReq = {
            orchestratorId: bundleOrch.id,
            _actualparams: [{
              sequence_type_id: bundleOrch.orchsteps[0]._compid,
              params: { page, bundleType }
            }]
          };

          const res = await IafDataSource.runOrchestrator(bundleOrch.id, orchReq);
          totalPages = res._result.total_pages;
          const newEmails = res._result.uniqueEnrolleeEmails.filter(e => !allEnrollees.includes(e));
          allEnrollees.push(...newEmails);
          page++;
        }

        if (bundleType === 'free') {
          setFreeBundleEnrollees(allEnrollees);
        } else {
          setPaidBundleEnrollees(allEnrollees);
        }
      }
    } catch (err) {
      console.error('Error loading academy memberships:', err);
    } finally {
      setFetchingMembership(false);
    }
  };

  const loadUsersForGroup = async () => {
    if (!selectedUserGroup) return;
    
    setLoadingUsers(true);
    setLoadingInvites(true);
    setUsersInSelectedGroup([]);
    setFilteredUsers([]);
    setInvitesInSelectedGroup([]);
    setExpiredInvites([]);

    try {
      if (config.useInternalGroups) {
        // Load users from internal groups
        const ctx = createCtx(selectedUserGroup._namespaces || []);
        const users = await IafUserGroup.getUsers(selectedUserGroup, ctx);
        // Handle both array and _list response formats
        const userList = Array.isArray(users) ? users : (users?._list || []);
        const sortedUsers = userList.sort((a, b) => a._firstname?.localeCompare(b._firstname) || 0);
        const uniqueUsers = _.uniqBy(sortedUsers, '_id');
        setUsersInSelectedGroup(uniqueUsers);
        setFilteredUsers(uniqueUsers);
        setTotalUsers(uniqueUsers.length);

        // Load invites
        const invites = await IafUserGroup.getInvites(selectedUserGroup, ctx);
        setInvitesInSelectedGroup(invites._list?.filter(i => i._status === 'PENDING') || []);
        setExpiredInvites(invites._list?.filter(i => i._status === 'EXPIRED') || []);
      } else if (scripts.fetchExternalUsersForGroup) {
        // Load users from external groups
        let users = await ScriptHelper.executeScript(scripts.fetchExternalUsersForGroup, { group: selectedUserGroup });
        users = users || [];
        users.sort((a, b) => a._firstname?.localeCompare(b._firstname) || 0);
        setUsersInSelectedGroup(users);
        setFilteredUsers(users);
        setTotalUsers(users.length);
      }
    } catch (err) {
      console.error('Error loading users:', err);
    } finally {
      setLoadingUsers(false);
      setLoadingInvites(false);
    }
  };

  const applyFilter = useCallback(() => {
    if (!filterBy || filterBy.length < 3) {
      setFilteredUsers(usersInSelectedGroup);
      return;
    }

    const filterUpper = filterBy.toUpperCase();
    
    if (filterUpper === 'FREE') {
      setFilteredUsers(usersInSelectedGroup.filter(u => freeBundleEnrollees.includes(u._email)));
    } else if (filterUpper === 'PAID') {
      setFilteredUsers(usersInSelectedGroup.filter(u => paidBundleEnrollees.includes(u._email)));
    } else {
      setFilteredUsers(usersInSelectedGroup.filter(u => 
        u._firstname?.toUpperCase().includes(filterUpper) ||
        u._lastname?.toUpperCase().includes(filterUpper) ||
        u._email?.toUpperCase().includes(filterUpper)
      ));
    }
  }, [filterBy, usersInSelectedGroup, freeBundleEnrollees, paidBundleEnrollees]);

  const handleGroupSelect = (group) => {
    if (!loadingUsers) {
      setSelectedUserGroup(group);
      setFilterBy('');
      setEditingName(false);
    }
  };

  const startEditingName = () => {
    setEditedName(selectedUserGroup._name);
    setNameError(null);
    setEditingName(true);
  };

  const cancelEditingName = () => {
    setEditingName(false);
    setNameError(null);
  };

  const saveGroupName = async () => {
    if (!editedName.trim()) {
      setNameError("Group name cannot be blank");
      return;
    }
    if (allUserGroupNamesUpper.includes(editedName.toUpperCase()) && 
        editedName.toUpperCase() !== selectedUserGroup._name.toUpperCase()) {
      setNameError("A group with this name already exists");
      return;
    }

    setSavingName(true);
    try {
      const updatedGroup = { ...selectedUserGroup, _name: editedName };
      const result = await IafUserGroup.update(updatedGroup);
      setSelectedUserGroup(result);
      await loadUserGroups();
      setEditingName(false);
    } catch (err) {
      setNameError(err.message || "Failed to save group name");
    } finally {
      setSavingName(false);
    }
  };

  const copyEmailsToClipboard = () => {
    const emails = filteredUsers.map(u => u._email).join(';');
    copyToClipboard(emails, 'User emails');
  };

  const confirmRemoveUser = (user) => {
    setDeletingUser(user);
  };

  const cancelRemoveUser = () => {
    setDeletingUser(null);
  };

  const removeUser = async (userToRemove) => {
    if (!userToRemove || !selectedUserGroup) return;

    try {
      if (config.useInternalGroups) {
        await IafUserGroup.deleteUserFromGroup(selectedUserGroup, userToRemove);
      }

      if (scripts.externalGroupsRemove && config.useExternalGroups?.includes(selectedUserGroup._shortName)) {
        await ScriptHelper.executeScript(scripts.externalGroupsRemove, {
          removeUser: userToRemove,
          fromGroup: selectedUserGroup
        });
      }

      await loadUsersForGroup();
    } catch (err) {
      console.error('Error removing user:', err);
    } finally {
      setDeletingUser(null);
    }
  };

  // Add user form handlers
  const emailIsValid = (email) => {
    const re = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return re.test(email);
  };

  const addEmailToList = () => {
    setAddFormError(null);
    
    if (!addFormFirstname.trim() || !addFormLastname.trim()) {
      setAddFormError("First and last name are required");
      return;
    }
    if (!emailIsValid(addFormEmail)) {
      setAddFormError("Invalid email address");
      return;
    }
    if (addFormEmails.find(e => e._email === addFormEmail.toLowerCase())) {
      setAddFormError("Email already in list");
      return;
    }

    setAddFormEmails([
      ...addFormEmails,
      { _email: addFormEmail.toLowerCase(), _firstname: addFormFirstname.trim(), _lastname: addFormLastname.trim() }
    ]);
    setAddFormFirstname('');
    setAddFormLastname('');
    setAddFormEmail('');
  };

  const removeEmailFromList = (email) => {
    setAddFormEmails(addFormEmails.filter(e => e._email !== email));
  };

  const submitAddUsers = async () => {
    if (addFormEmails.length === 0 || addFormGroups.length === 0) {
      setAddFormError("Please add at least one email and select at least one group");
      return;
    }

    setAddingUsers(true);
    setAddFormError(null);
    setAddFormSuccess(null);

    try {
      const successfulUsers = [];

      for (const group of addFormGroups) {
        for (const emailObj of addFormEmails) {
          try {
            if (config.useInternalGroups) {
              await IafPassSvc.addUsersToGroup(group.ug._id, [emailObj._email]);
            }
            if (!successfulUsers.find(u => u._email === emailObj._email)) {
              successfulUsers.push(emailObj);
            }
          } catch (err) {
            console.error(`Error adding ${emailObj._email} to ${group.label}:`, err);
          }
        }
      }

      // Handle external groups
      if (scripts.externalGroupsAdd && successfulUsers.length > 0) {
        await ScriptHelper.executeScript(scripts.externalGroupsAdd, {
          successfulUsers,
          groups: addFormGroups
        });
      }

      setAddFormSuccess(`Successfully added ${successfulUsers.length} user(s)`);
      setAddFormEmails([]);
      await loadUsersForGroup();
    } catch (err) {
      setAddFormError(err.message || "Failed to add users");
    } finally {
      setAddingUsers(false);
    }
  };

  const clearAddForm = () => {
    setAddFormFirstname('');
    setAddFormLastname('');
    setAddFormEmail('');
    setAddFormEmails([]);
    setAddFormGroups([]);
    setAddFormError(null);
    setAddFormSuccess(null);
  };

  const groupOptions = useMemo(() => 
    userGroups.map(ug => ({ label: ug._name, value: ug._id, ug })),
    [userGroups]
  );

  // Check if user has external account badges
  const getUserBadges = (userEmail) => {
    const badges = [];
    if (config.useInternalGroups && config.useExternalGroups?.includes(selectedUserGroup?._shortName)) {
      badges.push({ icon: 'fas fa-file-alt', label: 'Docs Access', color: '#60a5fa' });
    }
    if (freeBundleEnrollees.includes(userEmail)) {
      badges.push({ icon: 'fas fa-graduation-cap', label: 'Free Bundle', color: '#a78bfa' });
    }
    if (paidBundleEnrollees.includes(userEmail)) {
      badges.push({ icon: 'fas fa-dollar-sign', label: 'Paid Bundle', color: '#fbbf24' });
    }
    return badges;
  };

  return (
    <div className="user-group-page">
      {/* Header */}
      <div className="page-header">
        <div className="header-title">
          <i className="fas fa-users"></i>
          <h1>{config.title || 'User Groups'}</h1>
        </div>
        <p className="header-subtitle">
          Manage user groups and their members
        </p>
        {fetchingMembership && (
          <div className="fetching-badge">
            <i className="fas fa-spinner fa-spin"></i>
            <span>Loading external memberships...</span>
          </div>
        )}
      </div>

      {/* Messages */}
      {addFormSuccess && (
        <div className="message-banner success">
          <i className="fas fa-check-circle"></i>
          <span>{addFormSuccess}</span>
          <button onClick={() => setAddFormSuccess(null)}>×</button>
        </div>
      )}

      <div className="user-group-content">
        {/* Sidebar - Group List */}
        <div className="groups-sidebar">
          <div className="sidebar-header">
            <h2>
              <i className="fas fa-layer-group"></i>
              Groups
            </h2>
            {config.allowManageUsers && (
              <button className="btn-icon" onClick={() => setShowAddForm(!showAddForm)} title="Add Users">
                <i className={showAddForm ? "fas fa-times" : "fas fa-user-plus"}></i>
              </button>
            )}
          </div>

          {/* Add User Form (Collapsible) */}
          {showAddForm && (
            <div className="add-user-form">
              <h3>Add Users to Groups</h3>
              
              <div className="form-group">
                <label>Select Groups</label>
                <Select
                  styles={selectStyles}
                  options={groupOptions}
                  value={addFormGroups}
                  onChange={setAddFormGroups}
                  isMulti
                  closeMenuOnSelect={false}
                  placeholder="Select groups..."
                />
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>First Name</label>
                  <input
                    type="text"
                    value={addFormFirstname}
                    onChange={(e) => setAddFormFirstname(e.target.value)}
                    placeholder="First name"
                    disabled={addingUsers}
                  />
                </div>
                <div className="form-group">
                  <label>Last Name</label>
                  <input
                    type="text"
                    value={addFormLastname}
                    onChange={(e) => setAddFormLastname(e.target.value)}
                    placeholder="Last name"
                    disabled={addingUsers}
                  />
                </div>
              </div>

              <div className="form-group">
                <label>Email</label>
                <div className="input-with-button">
                  <input
                    type="email"
                    value={addFormEmail}
                    onChange={(e) => setAddFormEmail(e.target.value)}
                    placeholder="user@example.com"
                    disabled={addingUsers}
                    onKeyDown={(e) => e.key === 'Enter' && addEmailToList()}
                  />
                  <button className="btn-small" onClick={addEmailToList} disabled={addingUsers}>
                    <i className="fas fa-plus"></i>
                  </button>
                </div>
              </div>

              {addFormError && (
                <div className="form-error">{addFormError}</div>
              )}

              {addFormEmails.length > 0 && (
                <div className="email-list">
                  <label>Users to add ({addFormEmails.length})</label>
                  <ul>
                    {addFormEmails.map(e => (
                      <li key={e._email}>
                        <span>{e._firstname} {e._lastname}</span>
                        <span className="email">{e._email}</span>
                        <button onClick={() => removeEmailFromList(e._email)}>
                          <i className="fas fa-times"></i>
                        </button>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              <div className="form-actions">
                <button className="btn-secondary" onClick={clearAddForm} disabled={addingUsers}>
                  Clear
                </button>
                <button 
                  className="btn-primary" 
                  onClick={submitAddUsers} 
                  disabled={addingUsers || addFormEmails.length === 0 || addFormGroups.length === 0}
                >
                  {addingUsers ? <><i className="fas fa-spinner fa-spin"></i> Adding...</> : 'Add Users'}
                </button>
              </div>
            </div>
          )}

          {/* Group List */}
          <div className="group-list">
            {loadingGroups ? (
              <div className="loading-state">
                <i className="fas fa-spinner fa-spin"></i>
                <span>Loading groups...</span>
              </div>
            ) : (
              userGroups.map(group => (
                <div
                  key={group._id}
                  className={clsx('group-item', selectedUserGroup?._id === group._id && 'active', loadingUsers && 'disabled')}
                  onClick={() => handleGroupSelect(group)}
                >
                  <div className="group-name">{group._name}</div>
                  {selectedUserGroup?._id === group._id && (
                    <i className="fas fa-chevron-right"></i>
                  )}
                </div>
              ))
            )}
          </div>
        </div>

        {/* Main Content - Group Details */}
        <div className="group-details">
          {selectedUserGroup ? (
            <>
              {/* Group Header */}
              <div className="details-header">
                {!editingName ? (
                  <div className="group-title">
                    <h2>{selectedUserGroup._name}</h2>
                    {config.allowUserGroupEdit && (
                      <button className="btn-icon-small" onClick={startEditingName} title="Edit name">
                        <i className="fas fa-pencil-alt"></i>
                      </button>
                    )}
                  </div>
                ) : (
                  <div className="group-title-edit">
                    <input
                      type="text"
                      value={editedName}
                      onChange={(e) => setEditedName(e.target.value)}
                      disabled={savingName}
                      maxLength={50}
                    />
                    <button className="btn-icon-small success" onClick={saveGroupName} disabled={savingName}>
                      <i className={savingName ? "fas fa-spinner fa-spin" : "fas fa-check"}></i>
                    </button>
                    <button className="btn-icon-small danger" onClick={cancelEditingName} disabled={savingName}>
                      <i className="fas fa-times"></i>
                    </button>
                  </div>
                )}
                {nameError && <div className="name-error">{nameError}</div>}
              </div>

              {/* Members Section */}
              <div className="members-section">
                <div className="section-header">
                  <div className="member-count">
                    <h3>
                      <i className="fas fa-user"></i>
                      Members
                      <span className="count-badge">{totalUsers}</span>
                      {filterBy.length >= 3 && filteredUsers.length !== totalUsers && (
                        <span className="filtered-count">({filteredUsers.length} shown)</span>
                      )}
                    </h3>
                    {config.useInternalGroups && (
                      <button className="btn-icon-small" onClick={copyEmailsToClipboard} title="Copy emails">
                        <i className="fas fa-copy"></i>
                      </button>
                    )}
                  </div>
                  <div className="search-box">
                    <i className="fas fa-search"></i>
                    <input
                      type="text"
                      value={filterBy}
                      onChange={(e) => setFilterBy(e.target.value)}
                      placeholder="Search by name, email, or type 'FREE'/'PAID'"
                      disabled={loadingUsers}
                    />
                    {filterBy && (
                      <button className="clear-search" onClick={() => setFilterBy('')}>
                        <i className="fas fa-times"></i>
                      </button>
                    )}
                  </div>
                </div>

                {loadingUsers ? (
                  <div className="loading-state">
                    <i className="fas fa-spinner fa-spin"></i>
                    <span>Loading members...</span>
                  </div>
                ) : filteredUsers.length === 0 ? (
                  <div className="empty-state">
                    <i className="fas fa-users-slash"></i>
                    <p>{filterBy ? 'No members match your search' : 'No members in this group'}</p>
                  </div>
                ) : (
                  <div className="user-list">
                    {filteredUsers.map(u => {
                      const badges = getUserBadges(u._email);
                      const isCurrentUser = u._id === user?._id;
                      const isDeleting = deletingUser?._id === u._id;

                      return (
                        <div key={u._id} className={clsx('user-card', isCurrentUser && 'current-user')}>
                          <div className="user-info">
                            <div className="user-avatar">
                              {u._firstname?.[0]?.toUpperCase() || u._email?.[0]?.toUpperCase()}
                            </div>
                            <div className="user-details">
                              <div className="user-name">
                                {u._firstname} {u._lastname}
                                {isCurrentUser && <span className="you-badge">You</span>}
                              </div>
                              <div className="user-email">{u._email}</div>
                            </div>
                          </div>
                          
                          <div className="user-badges">
                            {badges.map((badge, idx) => (
                              <span key={idx} className="badge" style={{ color: badge.color }} title={badge.label}>
                                <i className={badge.icon}></i>
                              </span>
                            ))}
                          </div>

                          {config.allowManageUsers && !isDeleting && (
                            <div className="user-actions">
                              <button 
                                className="btn-icon-small danger" 
                                onClick={() => confirmRemoveUser(u)}
                                title="Remove from group"
                              >
                                <i className="fas fa-trash"></i>
                              </button>
                            </div>
                          )}

                          {isDeleting && (
                            <div className="delete-confirmation">
                              <span>Remove user?</span>
                              <button className="btn-small danger" onClick={() => removeUser(u)}>
                                <i className="fas fa-check"></i> Yes
                              </button>
                              <button className="btn-small" onClick={cancelRemoveUser}>
                                <i className="fas fa-times"></i> No
                              </button>
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>

              {/* Invites Section (only for internal groups with invite management) */}
              {config.allowManageInvites && config.useInternalGroups && (
                <div className="invites-section">
                  <div className="section-header">
                    <h3>
                      <i className="fas fa-envelope"></i>
                      Pending Invites
                      <span className="count-badge">{invitesInSelectedGroup.length}</span>
                    </h3>
                  </div>

                  {loadingInvites ? (
                    <div className="loading-state">
                      <i className="fas fa-spinner fa-spin"></i>
                      <span>Loading invites...</span>
                    </div>
                  ) : invitesInSelectedGroup.length === 0 ? (
                    <div className="empty-state small">
                      <p>No pending invites</p>
                    </div>
                  ) : (
                    <div className="invite-list">
                      {invitesInSelectedGroup.map(invite => (
                        <div key={invite._id} className="invite-card">
                          <div className="invite-email">{invite._email}</div>
                          <div className="invite-meta">
                            Expires: {new Date(invite._expireTime).toLocaleDateString()}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}

                  {expiredInvites.length > 0 && (
                    <>
                      <h4 className="expired-header">Expired Invites ({expiredInvites.length})</h4>
                      <div className="invite-list expired">
                        {expiredInvites.map(invite => (
                          <div key={invite._id} className="invite-card expired">
                            <div className="invite-email">{invite._email}</div>
                            <div className="invite-meta">
                              Expired: {new Date(invite._expireTime).toLocaleDateString()}
                            </div>
                          </div>
                        ))}
                      </div>
                    </>
                  )}
                </div>
              )}
            </>
          ) : (
            <div className="empty-state">
              <i className="fas fa-users"></i>
              <p>Select a group to view its members</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default DevUserGroupView;
